/**
 * ActivityAuditManager — Manager Activity Audit view (rough scaffold).
 *
 * LOCKED DECISIONS:
 *   A1  Scaffold only
 *   A4  Drilldown via sub-route /#/activity/drilldown?kpi=&window=&role=manager
 *   A5  Executive view has ZERO individual attribution (Manager view is separate)
 *   A8  Tab within Manager dashboard
 *
 * TENANT ISOLATION: tenant_id != 2 enforced (mock data: tenant_id: 1 only)
 * DATA STATUS: Mock/seeded.
 *
 * 5 KPI Cards: Team Conversations · Team Meetings Booked · Team Qualified-Forward · Deals Advanced · Deals Closed
 * Per-Rep Grid: One row per direct report with sparkline
 * Chart:       Stacked bar — team aggregate
 */

import { useState, useEffect, useMemo } from "react";
import { useLocation } from "wouter";
import { tokens } from "@/components/operator/tokens";
import AuditWindowSelector from "./AuditWindowSelector";
import AuditKpiCard from "./AuditKpiCard";
import AuditStackedBarChart from "./AuditStackedBarChart";
import { supabase, getSession } from "@/lib/session";
import type { TimeWindow } from "./audit-mock-data";

// Track D rebuild (B10, 2026-05-14): removed MANAGER_REP_SUMMARY, MANAGER_KPI_MOCK,
// BDR_CHART_WEEK from non-demo render path. Real data from activity_log + users.
// MANAGER_REP_SUMMARY had fictional user IDs that do not exist in Supabase.
// Demo users seeded 2026-05-14 (see TRACK_D_COMPLETE.md).

interface RepSummaryRow {
  user_id: string;
  email: string;
  role: string;
  conversations: number;
  meetings: number;
  qualified_fwd: number;
}

function windowStartMgr(w: TimeWindow): Date {
  const now = new Date();
  if (w === "today") { const d = new Date(now); d.setHours(0,0,0,0); return d; }
  if (w === "week")  { const d = new Date(now); d.setDate(d.getDate() - 7); return d; }
  if (w === "month") { const d = new Date(now); d.setDate(d.getDate() - 30); return d; }
  const d = new Date(now); d.setDate(d.getDate() - 365); return d;
}

function SparklineBar({ data }: { data: number[] }) {
  const max = Math.max(...data, 1);
  return (
    <div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 24 }}>
      {data.map((v, i) => (
        <div
          key={i}
          style={{
            width: 6,
            height: `${Math.max(4, (v / max) * 24)}px`,
            background: tokens.color.black,
            borderRadius: 2,
            opacity: i === data.length - 1 ? 1 : 0.4,
          }}
        />
      ))}
    </div>
  );
}

function DeltaBadge({ curr, prior }: { curr: number | null; prior: number }) {
  if (curr == null) return <span style={{ color: tokens.color.textSubtle, fontSize: 11 }}>—</span>;
  const diff = curr - prior;
  const color = diff > 0 ? tokens.color.success : diff < 0 ? tokens.color.danger : tokens.color.textSubtle;
  const arrow = diff > 0 ? "▲" : diff < 0 ? "▼" : "—";
  return (
    <span style={{ fontSize: 11, color, fontWeight: 600 }}>
      {arrow} {diff > 0 ? "+" : ""}{diff}
    </span>
  );
}

export default function ActivityAuditManager() {
  const [window, setWindow] = useState<TimeWindow>("today");
  const [, setLocation] = useLocation();
  const [reps, setReps] = useState<RepSummaryRow[]>([]);
  const [loading, setLoading] = useState(true);

  const session = getSession();
  const tenantId = session?.tenantId ?? 1;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const sb = supabase();
        const since = windowStartMgr(window).toISOString();
        // Fetch users and their activity counts
        const { data: users } = await sb
          .from("users")
          .select("id, email, role")
          .eq("tenant_id", tenantId)
          .neq("tenant_id", 2)
          .in("role", ["bdr", "ae"]);
        const { data: logs } = await sb
          .from("activity_log")
          .select("user_id, channel, action")
          .eq("tenant_id", tenantId)
          .neq("tenant_id", 2)
          .gte("created_at", since);
        if (!cancelled && users) {
          const logsByUser = new Map<string, { channel: string | null; action: string | null }[]>();
          for (const l of (logs ?? []) as any[]) {
            if (!logsByUser.has(l.user_id)) logsByUser.set(l.user_id, []);
            logsByUser.get(l.user_id)!.push(l);
          }
          const repRows: RepSummaryRow[] = (users as any[]).map(u => {
            const ul = logsByUser.get(u.id) ?? [];
            return {
              user_id: u.id,
              email: u.email,
              role: u.role,
              conversations: ul.filter(l => l.action && ["connected", "replied", "booked"].some(k => l.action!.toLowerCase().includes(k))).length,
              meetings: ul.filter(l => l.action && l.action.toLowerCase().includes("booked")).length,
              qualified_fwd: ul.filter(l => l.action && l.action.toLowerCase().includes("qualified")).length,
            };
          });
          setReps(repRows);
        }
      } catch {
        if (!cancelled) setReps([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [window, tenantId]);

  const kpis = useMemo(() => ({
    team_conversations: reps.reduce((s, r) => s + r.conversations, 0),
    team_meetings_booked: reps.reduce((s, r) => s + r.meetings, 0),
    team_qualified_forward: reps.reduce((s, r) => s + r.qualified_fwd, 0),
    deals_advanced: 0,
    deals_closed: 0,
  }), [reps]);
  const priorKpis = { team_conversations: 0, team_meetings_booked: 0, team_qualified_forward: 0, deals_advanced: 0, deals_closed: 0 };

  function drillRep(userId: string, role: string) {
    const roleKey = role.toLowerCase() as "bdr" | "ae";
    setLocation(`/activity/drilldown?kpi=conversations&window=${window}&role=${roleKey}&rep=${userId}`);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Status banner — honest about data state (Track D B10 patch) */}
      {loading && (
        <div style={{ background: "#FAFAF7", border: "1px solid #EFEFEE", borderRadius: 6, padding: "7px 14px", fontSize: 12, color: "#8E8E94" }}>
          Loading team activity...
        </div>
      )}
      {!loading && reps.every(r => r.conversations === 0) && (
        <div style={{ background: "#F0F9FF", border: "1px solid #BAE6FD", borderRadius: 6, padding: "7px 14px", fontSize: 12, color: "#0369A1", fontWeight: 500 }}>
          No team activity logged for this window. Activity appears here once the log write paths are connected (C5/C6/C7).
        </div>
      )}

      {/* Zone 1 — Window selector */}
      <AuditWindowSelector active={window} onChange={setWindow} />

      {/* Zone 2 — Team KPI cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
        <AuditKpiCard label="Team Conversations"     value={kpis.team_conversations}    prior={priorKpis.team_conversations}    kpiKey="team_conversations"    window={window} drillRole="manager" />
        <AuditKpiCard label="Meetings Booked"        value={kpis.team_meetings_booked}  prior={priorKpis.team_meetings_booked}  kpiKey="team_meetings_booked"  window={window} drillRole="manager" />
        <AuditKpiCard label="Qualified-Forward"      value={kpis.team_qualified_forward}prior={priorKpis.team_qualified_forward}kpiKey="team_qualified_forward" window={window} drillRole="manager" />
        <AuditKpiCard label="Deals Advanced (Team)"  value={kpis.deals_advanced}        prior={priorKpis.deals_advanced}        kpiKey="deals_advanced"        window={window} drillRole="manager" />
        <AuditKpiCard label="Deals Closed (Team)"    value={kpis.deals_closed}          prior={priorKpis.deals_closed}          kpiKey="deals_closed"          window={window} drillRole="manager" />
      </div>

      {/* Zone 3 — Chart (BUILD-14: bars are clickable drill-downs) */}
      <div
        style={{
          background: "#FFFFFF", border: `1px solid ${tokens.color.border}`,
          borderRadius: tokens.radius.card, padding: "18px 20px",
        }}
      >
        <AuditStackedBarChart
          data={[]}
          title="Team Activity Volume"
          onBarClick={(series, label) => {
            const kpiMap: Record<string, string> = {
              calls: "calls_made",
              emails: "emails_sent",
              convos: "conversations",
              meetings: "meetings_booked",
            };
            setLocation(`/activity/drilldown?kpi=${kpiMap[series] ?? series}&window=${window}&role=manager&label=${encodeURIComponent(label)}`);
          }}
        />
      </div>

      {/* Per-rep grid */}
      <div>
        <div style={{ fontSize: 14, fontWeight: 700, color: tokens.color.textPrimary, marginBottom: 10 }}>
          Team Breakdown
        </div>

        <div
          style={{
            background: "#FFFFFF", border: `1px solid ${tokens.color.border}`,
            borderRadius: tokens.radius.card, overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 70px 100px 90px 100px 110px 100px 80px 80px",
              gap: 10, padding: "9px 16px",
              borderBottom: `1px solid ${tokens.color.border}`,
              background: tokens.color.surfaceMute,
            }}
          >
            {["Name", "Role", "Conversations", "Meetings", "Qual-Fwd", "Deals Adv", "Deals Closed", "vs Prior", "Trend"].map((h) => (
              <div key={h} style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: tokens.color.textSubtle }}>
                {h}
              </div>
            ))}
          </div>

          {reps.length === 0 && !loading ? (
            <div style={{ padding: "24px 16px", textAlign: "center", fontSize: 13, color: tokens.color.textSubtle }}>
              No direct reports found. Users appear here once team roles are assigned.
            </div>
          ) : null}
          {reps.map((rep) => (
            <div
              key={rep.user_id}
              onClick={() => drillRep(rep.user_id, rep.role)}
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 70px 100px 90px 100px 110px 100px 80px 80px",
                gap: 10, padding: "12px 16px",
                borderBottom: `1px solid ${tokens.color.borderSoft}`,
                alignItems: "center",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.background = tokens.color.surfaceMute; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
            >
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: tokens.color.textPrimary }}>{rep.email}</div>
              </div>
              <div
                style={{
                  fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
                  textTransform: "uppercase", padding: "2px 7px", borderRadius: 999,
                  background: rep.role === "BDR" ? "#EFEFEE" : "#FEE2E2",
                  color: rep.role === "BDR" ? "#0B0B0F" : "#DC2626",
                  width: "fit-content",
                }}
              >
                {rep.role}
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: tokens.color.textPrimary }}>
                {rep.conversations}
              </div>
              <div style={{ fontSize: 13, color: tokens.color.textPrimary }}>{rep.meetings}</div>
              <div style={{ fontSize: 13, color: tokens.color.textPrimary }}>
                {rep.qualified_fwd}
              </div>
              <div style={{ fontSize: 13, color: tokens.color.textPrimary }}>—</div>
              <div style={{ fontSize: 13, color: tokens.color.textPrimary }}>—</div>
              <DeltaBadge curr={rep.conversations} prior={0} />
              <SparklineBar data={[0]} />
            </div>
          ))}
        </div>

        <div style={{ fontSize: 11, color: tokens.color.textSubtle, marginTop: 8 }}>
          Click any row to view that team member's individual activity detail.
        </div>
      </div>
    </div>
  );
}
